import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline'

import { updateConfig } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'
import WidgetEditModal from '../components/WidgetEditModal'

const WidgetsConfig = ({ config }) => {
  const [editingWidget, setEditingWidget] = useState(null)
  const [showWidgetModal, setShowWidgetModal] = useState(false)
  // 添加选中状态管理
  const [selectedWidget, setSelectedWidget] = useState(null)

  const queryClient = useQueryClient()

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClearSelection()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // 处理小部件选中
  const handleSelectWidget = (widgetIndex, event) => {
    event.stopPropagation()
    if (selectedWidget === widgetIndex) {
      setSelectedWidget(null)
    } else {
      setSelectedWidget(widgetIndex)
    }
  }

  // 清除选中状态
  const handleClearSelection = () => {
    setSelectedWidget(null)
  }

  const updateConfigMutation = useMutation({
    mutationFn: (data) => updateConfig('widgets', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('小部件配置已更新')
    },
    onError: (error) => {
      toast.error(`更新失败: ${error.message}`)
    },
  })

  const moveWidget = (index, direction) => {
    // 安全检查
    if (!config || !Array.isArray(config)) {
      toast.error('配置数据无效')
      return
    }

    if (index < 0 || index >= config.length) {
      toast.error('无效的小部件索引')
      return
    }

    const newConfig = [...config]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    if (targetIndex >= 0 && targetIndex < newConfig.length) {
      // 确保两个索引位置的数据都存在
      if (!newConfig[index] || !newConfig[targetIndex]) {
        toast.error('小部件数据缺失')
        return
      }

      // 设置当前小部件为选中状态
      setSelectedWidget(index)

      // 安全的交换操作
      const temp = newConfig[index]
      newConfig[index] = newConfig[targetIndex]
      newConfig[targetIndex] = temp

      updateConfigMutation.mutate(newConfig, {
        onSuccess: () => {
          // 移动后，选中状态跟随到新位置
          setSelectedWidget(targetIndex)
        }
      })
    } else {
      toast.error('无法移动到指定位置')
    }
  }

  const deleteWidget = (index) => {
    if (confirm('确定要删除这个小部件吗？')) {
      const newConfig = [...config]
      newConfig.splice(index, 1)
      updateConfigMutation.mutate(newConfig)
    }
  }

  const handleEditWidget = (widget, index) => {
    setEditingWidget({ widget, index })
    setShowWidgetModal(true)
  }

  const handleAddWidget = () => {
    setEditingWidget({ widget: null, index: -1 })
    setShowWidgetModal(true)
  }

  const handleSaveWidget = (widgetData, index) => {
    const newConfig = [...config]

    if (index === -1) {
      // 添加新小部件
      newConfig.push(widgetData)
    } else {
      // 更新现有小部件
      newConfig[index] = widgetData
    }

    updateConfigMutation.mutate(newConfig)
  }

  const getWidgetDisplayName = (widget) => {
    const type = Object.keys(widget)[0]
    // 精简后的小部件类型名称
    const typeNames = {
      resources: '系统资源',
      search: '搜索框',
      datetime: '日期时间',
      greeting: '问候语',
      logo: 'Logo',
      customapi: '自定义 API'
    }
    return typeNames[type] || type
  }

  const getWidgetDescription = (widget) => {
    const type = Object.keys(widget)[0]
    const config = widget[type]

    let description = ''

    // 精简后的小部件描述逻辑
    switch (type) {
      case 'resources':
        description = `监控: ${[
          config.cpu && 'CPU',
          config.memory && '内存',
          config.disk && '磁盘',
          config.cputemp && '温度'
        ].filter(Boolean).join(', ')}`
        break
      case 'search':
        description = `搜索引擎: ${config.provider || 'duckduckgo'}`
        break
      case 'datetime':
        description = '显示当前时间和日期'
        break
      case 'greeting':
        description = config.text || '显示问候语'
        break
      case 'logo':
        description = config.icon ? `图标: ${config.icon}` : 'Logo 小部件'
        break
      case 'customapi':
        description = config.url ? `API: ${config.url}` : '自定义 API 小部件'
        break
      default:
        description = '自定义小部件'
    }

    // 添加链接信息
    if (config.href) {
      description += ` | 🔗 链接: ${config.href}`
    }

    return description
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6" onClick={handleClearSelection}>
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">小部件配置</h1>
          <p className="mt-2 text-gray-400">
            管理导航页上的小部件和信息面板
          </p>
          <p className="mt-1 text-xs text-gray-500">
            💡 点击小部件可选中并高亮显示，按 ESC 键或点击空白区域取消选中
          </p>
        </div>
        <button
          onClick={handleAddWidget}
          className="btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          添加小部件
        </button>
      </div>

      {/* 小部件列表 */}
      <div className="space-y-4">
        {config.map((widget, index) => {
          const isWidgetSelected = selectedWidget === index

          return (
            <div
              key={index}
              className={`card transition-all duration-300 cursor-pointer ${isWidgetSelected
                ? 'ring-2 ring-green-400 bg-gray-800/80 enhanced-glow-green glow-selected-service'
                : 'hover:bg-gray-800/50'
                }`}
              onClick={(e) => handleSelectWidget(index, e)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">
                    {getWidgetDisplayName(widget)}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {getWidgetDescription(widget)}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  {/* 移动按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveWidget(index, 'up')
                    }}
                    disabled={index === 0}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveWidget(index, 'down')
                    }}
                    disabled={index === config.length - 1}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowDownIcon className="h-4 w-4" />
                  </button>

                  {/* 编辑和删除按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditWidget(widget, index)
                    }}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteWidget(index)
                    }}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {config.length === 0 && (
          <div className="card text-center py-12">
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              暂无小部件配置
            </h3>
            <p className="text-gray-400 mb-4">
              添加你的第一个小部件来丰富导航页
            </p>
            <button
              onClick={handleAddWidget}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              添加小部件
            </button>
          </div>
        )}
      </div>

      {/* 小部件链接功能说明 */}
      <div className="card">
        <h3 className="card-title">🔗 小部件链接功能</h3>
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <h4 className="font-medium text-blue-300 mb-3">新功能：现在所有小部件都支持添加可点击链接，增强交互性和实用性。</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <h5 className="font-medium text-blue-300 mb-3">🎯 使用场景示例：</h5>
              <ul className="space-y-2 text-sm text-blue-200">
                <li>• <strong>天气小部件</strong> → 链接到详细天气预报</li>
                <li>• <strong>系统资源</strong> → 链接到监控面板</li>
                <li>• <strong>时钟小部件</strong> → 链接到日历应用</li>
                <li>• <strong>问候语</strong> → 链接到个人主页</li>
              </ul>
            </div>

            <div>
              <h5 className="font-medium text-blue-300 mb-3">⚙️ 配置选项：</h5>
              <ul className="space-y-2 text-sm text-blue-200">
                <li>• <strong>链接地址</strong>：任何有效的 URL</li>
                <li>• <strong>打开方式</strong>：新标签页、当前页面等</li>
                <li>• <strong>可选配置</strong>：不影响小部件原有功能</li>
                <li>• <strong>智能显示</strong>：配置列表中显示链接信息</li>
              </ul>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-800/30 rounded">
            <p className="text-xs text-blue-300">
              💡 <strong>提示</strong>：链接功能完全可选，您可以为任何小部件添加链接，也可以保持原有的纯展示功能。
              配置时只需填写链接地址，系统会自动处理点击事件和页面跳转。
            </p>
          </div>
        </div>
      </div>

      {/* 小部件编辑模态框 */}
      <WidgetEditModal
        isOpen={showWidgetModal}
        onClose={() => {
          setShowWidgetModal(false)
          setEditingWidget(null)
        }}
        onSave={handleSaveWidget}
        widget={editingWidget?.widget}
        index={editingWidget?.index !== undefined ? editingWidget.index : -1}
      />
    </div>
  )
}

export default WidgetsConfig
